import {useSelector} from "react-redux";
import {COMMENT_SAVING_STATUS, COMMENTS_INPUT_MAX_LENGTH} from "utils/constants";
import * as reduxSelectors from "components/store/application.reducers";
import {useTranslation} from "components/translations/useTranslation";
import {TextArea} from "components/common/TextArea.component";
import {Button, ButtonType} from "components/common/Button.component";
import type {CommentsPanelProps} from "types/taskDetailsComments";
import CommentsList from "./CommentsList.component";
import CommentsValidation from "./CommentsValidation.component";
import ModuleMenu from "components/taskdetails/navigation/ModuleMenu.component";
import "../../style/comments.css";

const MAX_SHOWN_COUNT = 999;
const COMMENT_INPUT_ID = "draftCommentInput";

/**
 * The comments module of the redesigned task details page: the count in the title, the comments,
 * and, unless read-only, the field for the draft comment with its saving state, a note that comments
 * are visible to the whole workflow and, where comments can be added on their own, the button to add it.
 * It replaces taskdetails-remake/panels/draftcomments/CommentsPanel.component on the redesigned page.
 */
export const CommentsPanel = ({readOnly, offline, initialCommentsCount, allowExtraComments, onChange, onSaveComment}: CommentsPanelProps) => {
    const {translate} = useTranslation();
    const comment: string | undefined = useSelector(reduxSelectors.getTaskDraftCommentText);
    const savingStatus: number = useSelector(reduxSelectors.getTaskDraftCommentsSavingStatus);
    const commentsCount: number = useSelector(reduxSelectors.getTaskDraftCommentsCount);
    const isLoading: boolean = useSelector(reduxSelectors.getTaskDraftCommentsLoadingStatus);
    const isInputInvalid = (comment?.length ?? 0) > COMMENTS_INPUT_MAX_LENGTH;

    const isAddCommentDisabled = !comment ||
        savingStatus === COMMENT_SAVING_STATUS.START ||
        savingStatus === COMMENT_SAVING_STATUS.SAVING ||
        comment.length > COMMENTS_INPUT_MAX_LENGTH;

    // with extra comments the loaded comments are counted, otherwise the count the task came with
    const loadedCount = isLoading ? 0 : commentsCount;
    const count = allowExtraComments ? loadedCount : (initialCommentsCount || 0);

    return (
        <section className="comments-module" data-cy="taskDetail.commentsPanel" aria-labelledby="comments-module-title">
            <header className="comments-module__header">
                <h2 id="comments-module-title" className="comments-module__title">
                    {translate("taskDetail.comments")} - <span data-cy="taskDetail.comments.number">
                        {count <= MAX_SHOWN_COUNT ? count : `${MAX_SHOWN_COUNT}+`}
                    </span>
                </h2>
                <ModuleMenu dataCy="taskDetail.commentsPanel.menu"/>
            </header>
            <div className="comments-module__body">
                <CommentsList readOnly={readOnly}/>
                {!readOnly &&
                    <div className="comments-module__compose">
                        <label htmlFor={COMMENT_INPUT_ID} className="sr-only">
                            {translate("taskDetail.addCommentPlaceholder")}
                        </label>
                        <TextArea id={COMMENT_INPUT_ID}
                                  rows={3}
                                  value={comment || ""}
                                  placeholder={translate("taskDetail.addCommentPlaceholder")}
                                  isDisabled={offline}
                                  isInvalid={isInputInvalid}
                                  onChange={onChange}/>
                        <div className="comments-module__status">
                            <CommentsValidation comment={comment} offline={offline} savingStatus={savingStatus}/>
                        </div>
                        <div className="comments-module__foot">
                            <p className="comments-module__visibility">{translate("popUp.commentVisibleToEverybody")}</p>
                            {allowExtraComments &&
                                <div className="comments-module__submit" data-testid="addBtn">
                                    <Button type={ButtonType.PRIMARY}
                                            onClick={onSaveComment}
                                            disabled={isAddCommentDisabled}
                                            dataCy="taskDetail.comments.saveButton"
                                            text={translate("taskDetail.sendNewComment")}/>
                                </div>}
                        </div>
                    </div>}
            </div>
        </section>
    );
};

export default CommentsPanel;
